import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { errorMessage, get, patch } from '../../api/client';
import type { NewsItem, NewsList } from '../../api/types';
import { IconAlert, IconExternal, IconImage, IconPlus } from '../../components/icons';
import { PageHead, Spinner, Switch, useFeedback } from '../../components/ui';
import { useT } from '../../i18n';
import { LangChips, newsDate, objectPosition } from './shared';

/** Noticias del sitio de Phelon & Moore: el carrusel de la landing, /news/ y una página por noticia. */
export function PmNews() {
  const { t, tn } = useT();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const { data, isPending, error } = useQuery({ queryKey: ['pm-news', 'list'], queryFn: () => get<NewsList>('/admin/pm/news') });

  const setVisible = async (n: NewsItem, visible: boolean) => {
    try {
      const { news } = await patch<{ news: NewsItem }>(`/admin/pm/news/${n.id}`, { visible });
      qc.setQueryData(['pm-news', n.id], news);
      toast(visible ? t('Ahora es visible (se ve en el sitio al publicar)') : t('Quedó oculta (sale del sitio al publicar)'));
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
    void qc.invalidateQueries({ queryKey: ['pm-news', 'list'] });
    void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  // La API las da en el orden del sitio (de la más vieja a la más nueva); aquí, la más nueva arriba.
  const items = data ? [...data.items].reverse() : [];

  return (
    <>
      <PageHead
        kicker={t('Landing')}
        title={t('Noticias')}
        description={t('Las del carrusel de la landing, la portada de noticias y una página por noticia, en los seis idiomas del sitio. Los cambios se ven después de publicar.')}
      >
        {data?.siteUrl && (
          <a className="btn" href={`${data.siteUrl}/news/`} target="_blank" rel="noreferrer">
            <IconExternal /> {t('Ver en el sitio')}
          </a>
        )}
        <Link to="/noticias/nueva" className="btn btn-primary">
          <IconPlus /> {t('Nueva noticia')}
        </Link>
      </PageHead>

      {isPending ? (
        <Spinner />
      ) : error || !data ? (
        <div className="notice error">{errorMessage(error)}</div>
      ) : items.length === 0 ? (
        <div className="panel empty-state">
          <strong>{t('Todavía no hay noticias.')}</strong>
          <Link to="/noticias/nueva" className="btn btn-primary">
            <IconPlus /> {t('Nueva noticia')}
          </Link>
        </div>
      ) : (
        <div className="panel" style={{ overflow: 'hidden' }}>
          {items.map((n) => {
            const errors = n.issues.filter((i) => i.level === 'error').length;
            const warnings = n.issues.length - errors;
            return (
              <div key={n.id} className={`list-item news-row ${n.visible ? '' : 'is-hidden'}`}>
                <Link to={`/noticias/${n.id}`} className="thumb thumb-cover" aria-hidden="true" tabIndex={-1}>
                  {n.image ? <img src={n.image.url} alt="" loading="lazy" style={{ objectPosition: objectPosition(n.crop) }} /> : <IconImage />}
                </Link>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row row-wrap" style={{ gap: 8 }}>
                    <Link to={`/noticias/${n.id}`} className="news-title">
                      {n.texts.en.headline || n.slug}
                    </Link>
                    {!n.visible && <span className="badge">{t('Oculta')}</span>}
                    {errors > 0 && (
                      <span className="badge red" title={n.issues.map((i) => i.message).join(' · ')}>
                        <IconAlert width={12} /> {errors === 1 ? t('Falta algo') : t('Faltan {n} cosas', { n: errors })}
                      </span>
                    )}
                  </div>
                  <div className="faint news-meta">
                    {newsDate(n.date)} · {n.source}
                    <span className="mono"> · /news/{n.slug}/</span>
                  </div>
                  <div className="row row-wrap" style={{ gap: 8, marginTop: 6 }}>
                    <LangChips texts={n.texts} />
                    {warnings > 0 && (
                      <span className="faint" style={{ fontSize: 12 }} title={n.issues.filter((i) => i.level === 'warning').map((i) => i.message).join(' · ')}>
                        <IconAlert width={12} style={{ verticalAlign: '-2px', color: 'var(--warn)' }} /> {tn(warnings, '{n} aviso', '{n} avisos')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="actions">
                  <Switch checked={n.visible} onChange={(v) => void setVisible(n, v)} label={n.visible ? t('Visible') : t('Oculta')} />
                  <Link to={`/noticias/${n.id}`} className="btn btn-sm">
                    {t('Editar')}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {data && items.length > 0 && (
        <p className="faint" style={{ fontSize: 13, marginTop: 12 }}>
          {t('En el sitio van por fecha: el carrusel de la landing arranca por la más vieja y la portada de noticias destaca la más nueva.')}
        </p>
      )}
    </>
  );
}
